import { useRef } from "react";
import * as THREE from "three";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrthographicCamera } from "@react-three/drei";
import { Rocket3D } from "@/components/Rocket3D";
import { Ufo3D } from "@/components/Ufo3D";
import { Spaceship3D } from "./Spaceship3D";

export const MODEL_REGISTRY = {
  "53bb06ea-0eb2-4401-8100-f3d2423d4922": Ufo3D,
  "73d7e815-af2c-4519-b352-ce636d615e69": Rocket3D,
  "08876537-5036-4bcf-9127-58800d31acd8": Spaceship3D,
} as const;

export type ModelType = keyof typeof MODEL_REGISTRY;

export interface PlayerEntry {
  id: string;
  modelType: ModelType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  player: any;
}

interface RocketsOverlayProps {
  markerPositions: Array<{
    left: number;
    top: number;
    x: number;
    y: number;
    entryId: string;
    rotation: number;
  }>;
  playerEntries: PlayerEntry[];
  userPubkey?: string;
}

const RocketsController = ({
  markerPositions,
  playerEntries,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  userPubkey,
}: RocketsOverlayProps) => {
  const { size } = useThree();
  const groupsRef = useRef<(THREE.Group | null)[]>([]);
  // Store animation state: entryId -> { startPos, targetPos, startTime, startRot, targetRot }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const animState = useRef(new Map<string, any>());

  useFrame((state) => {
    const now = state.clock.elapsedTime;
    // Match the framer-motion duration: 1s linear
    const duration = 1.0;

    playerEntries.forEach((entry, index) => {
      const group = groupsRef.current[index];
      if (!group) return;

      const player = entry.player;
      
      // Check if player has activeBorderChartId that matches MODEL_REGISTRY
      const activeBorderChartId = player?.ownerProfile?.activeBorderChartId;
      const hasRocketItem =
        activeBorderChartId &&
        activeBorderChartId in MODEL_REGISTRY &&
        activeBorderChartId === entry.modelType;

      if (!hasRocketItem) {
        group.visible = false;
        return;
      }

      // Find the target position for this player
      const target = markerPositions.find((p) => p.entryId === entry.id);

      // If no target or invalid position, hide
      if (!target || target.left <= 0) {
        group.visible = false;
        return;
      }

      group.visible = true;

      // Calculate Target in Three.js Coords
      // Use relative x/y from chart, not page coordinates
      const targetX = target.x - size.width / 2;
      const targetY = -(target.y - size.height / 2);
      const targetRot = -target.rotation;

      // Initialize or Update Animation State
      let anim = animState.current.get(entry.id);

      if (!anim) {
        // First frame init
        anim = {
          startPos: new THREE.Vector3(targetX, targetY, 0),
          targetPos: new THREE.Vector3(targetX, targetY, 0),
          startRot: targetRot,
          targetRot: targetRot,
          startTime: now - duration, // Ensure it looks finished
        };
        group.position.set(targetX, targetY, 0);
        group.rotation.z = targetRot;
        animState.current.set(entry.id, anim);
      }

      // Check for significant target change to trigger new animation segment
      // Use a small threshold to avoid restarting animation on micro-fluctuations
      if (
        Math.abs(anim.targetPos.x - targetX) > 0.5 ||
        Math.abs(anim.targetPos.y - targetY) > 0.5 ||
        Math.abs(anim.targetRot - targetRot) > 0.1
      ) {
        anim.startPos.copy(group.position); // Start from CURRENT visual position for continuity
        anim.targetPos.set(targetX, targetY, 0);
        anim.startRot = group.rotation.z;
        anim.targetRot = targetRot;
        anim.startTime = now;
      }

      // Calculate Progress
      const elapsed = now - anim.startTime;
      const t = Math.min(elapsed / duration, 1.0);

      // Linear Interpolation (t) matches ease: "linear"
      group.position.lerpVectors(anim.startPos, anim.targetPos, t);

      // Rotation interpolation
      group.rotation.z = THREE.MathUtils.lerp(anim.startRot, anim.targetRot, t);
    });
  });

  return (
    <>
      {playerEntries.map((entry, i) => {
        const ModelComponent =
          MODEL_REGISTRY[entry.modelType] || MODEL_REGISTRY["53bb06ea-0eb2-4401-8100-f3d2423d4922"];
        return (
          <group key={entry.id} ref={(el) => (groupsRef.current[i] = el)}>
            <ModelComponent rotation={0} />
          </group>
        );
      })}
    </>
  );
};

export const RocketsOverlay = (props: RocketsOverlayProps) => {
  return (
    <div className="absolute inset-0 pointer-events-none z-51">
      <Canvas>
        <OrthographicCamera makeDefault position={[0, 0, 1000]} zoom={1} />
        <ambientLight position={[0, 0, 10]} intensity={3} />
        <directionalLight
          position={[0, THREE.MathUtils.degToRad(40), 0]}
          intensity={12}
        />
        <RocketsController {...props} />
      </Canvas>
    </div>
  );
};
